import { createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'
import { APIError, authenticatedRequest, getExceptionPayload } from '../axiosConfiguration'
import { AuthorizationsGroup, AuthorizationsGroupFormData } from '../type/authorizationsGroups'
import * as layoutActions from './layout'

export const getAuthorizationsGroups =
  createAsyncThunk<AuthorizationsGroup[], void, { rejectValue: APIError }>(
    'users/getAuthorizationsGroups',
    async (_, { dispatch, rejectWithValue }) => {
      dispatch(layoutActions.startLoading())

      try {
        const instance = await authenticatedRequest()

        const res = await instance.get<AuthorizationsGroup[]>(`/authorization/group`)

        dispatch(layoutActions.endLoading())
        return res.data
      } catch (error) {
        const err = getExceptionPayload(error, 'userManagement:error.getAuthorizationsGroups')

        dispatch(layoutActions.endLoading())
        dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

        return rejectWithValue(err)
      }
    },
  )

export const deleteAuthorizationsGroup =
  createAsyncThunk<void, number, { rejectValue: APIError }>(
    'users/deleteAuthorizationsGroup',
    async (groupId, { dispatch, rejectWithValue }) => {
      dispatch(layoutActions.startLoading())

      try {
        const instance = await authenticatedRequest()

        const queryParams = new URLSearchParams()
        queryParams.append('id', `${groupId}`)

        await instance.delete(`/authorization/group?${queryParams.toString()}`)

        dispatch(layoutActions.endLoading())
        dispatch(
          layoutActions.showToast({
            summary: i18next.t('userManagement:delete.auth_group.notification.success'),
            severity: 'success',
          }),
        )
      } catch (error) {
        const err = getExceptionPayload(error, 'userManagement:error.deleteAuthorizationsGroup')

        dispatch(layoutActions.endLoading())
        dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
        return rejectWithValue(err)
      }
    },
  )

export const addAuthorizationsGroup = createAsyncThunk<void, AuthorizationsGroupFormData, { rejectValue: APIError }>(
  'users/addAuthorizationsGroup',
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()

      await instance.post('/authorization/group', payload)

      dispatch(layoutActions.endLoading())
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:add.auth_group.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.addAuthorizationsGroup')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)


export const editAuthorizationsGroup = createAsyncThunk<void, AuthorizationsGroupFormData & { id: number }, {
  rejectValue: APIError
}>(
  'users/editAuthorizationsGroup',
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()

      await instance.put('/authorization/group', payload)

      dispatch(layoutActions.endLoading())
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('userManagement:edit.auth_group.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.editAuthorizationsGroup')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)

export const getAuthorizationsGroupInfo = createAsyncThunk<
  AuthorizationsGroupFormData,
  number,
  { rejectValue: APIError }
>('users/getAuthorizationsGroupInfo', async (groupId, { dispatch, rejectWithValue }) => {
  dispatch(layoutActions.startLoading())

  try {
    const instance = await authenticatedRequest()
    const queryParams = new URLSearchParams()
    queryParams.append('id', `${groupId}`)

    const res = await instance.get<AuthorizationsGroupFormData>(`/authorization/group?${queryParams.toString()}`)

    dispatch(layoutActions.endLoading())

    return res.data
  } catch (error) {
    const err = getExceptionPayload(error, 'userManagement:error.getAuthorizationsGroupInfo')

    dispatch(layoutActions.endLoading())
    dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
    return rejectWithValue(err)
  }
})
